import { TCustomPage, TProfilePage, TWarmUpPage, TWeightLossPage } from '@/config/types'
import { PageName } from '@/enums/pageName'
import { getTitleFromPageConfig } from '@/utils/pageUtils'
import { getPath } from '@/utils/urlUtils'

import { PreparingPlanType5 } from '@/components/preparingPlan'
import { sun } from '@/shared/emoji'
import {
  age18_20,
  age18_20_large,
  age36_45,
  age36_45_large,
  age46_55,
  age46_55_large,
  age55,
  age55_large,
  diet,
  level,
  lifestyle,
  person,
} from '@/shared/images/organic-fit'

import { WeightLossChartPilates } from '@/orgfit/components/weightLossChartPilates'
import { WeightLossTimelineChart } from '@/orgfit/components/weightLossTimelineChart'

import i18next from './../../i18n'
import { pagesConfig } from './selectionPages'
import { getItem } from '../storage'

const isRegistrationEnabled = window.config.CHECKOUT_SETTINGS?.registration_page_enabled

export const preparingPlanPage: TCustomPage = {
  nextUrl: isRegistrationEnabled ? getPath(PageName.registration) : getPath(PageName.warmUpScreen),
  title: i18next.t('preparingScreen.title'),
  PageContent: PreparingPlanType5,
  reviewsTitle: i18next.t('preparingScreen.reviewsTitle'),
  reviews: [
    {
      name: i18next.t('preparingScreen.reviews.0.name'),
      description: i18next.t('preparingScreen.reviews.0.description'),
    },
    {
      name: i18next.t('preparingScreen.reviews.1.name'),
      description: i18next.t('preparingScreen.reviews.1.description'),
    },
    {
      name: i18next.t('preparingScreen.reviews.2.name'),
      description: i18next.t('preparingScreen.reviews.2.description'),
    },
  ],
  policyBlock: true,
}

export const profilePage: TProfilePage = {
  title: i18next.t('profileScreen.title'),
  tabTitle: i18next.t('profileScreen.tabTitle', ''),
  nextUrl: getPath(PageName.event),
  bmiText: i18next.t('profileScreen.bmiText'),
  pronoun: i18next.t('profileScreen.pronoun'),
  bmiSection: {
    className: 'bmi-description',
    items: [
      {
        id: 1,
        iconNode: <img src={sun} alt="" />,
        title: i18next.t('profileScreen.bmiSection.title'),
        description: i18next.t('profileScreen.bmiSection.description'),
      },
    ],
  },
  bmiDescriptions: [
    i18next.t('profileScreen.bmiDescriptions.0'),
    i18next.t('profileScreen.bmiDescriptions.1'),
    i18next.t('profileScreen.bmiDescriptions.2'),
    i18next.t('profileScreen.bmiDescriptions.3'),
  ],
  images: {
    youngAdult: {
      slim: age18_20,
      large: age18_20_large,
    },
    adult: {
      slim: age36_45,
      large: age36_45_large,
    },
    middleAged: {
      slim: age46_55,
      large: age46_55_large,
    },
    elderly: {
      slim: age55,
      large: age55_large,
    },
  },
  personalData: {
    className: 'data-description',
    itemsF: () => {
      return [
        {
          id: 1,
          iconNode: <img src={person} alt="" />,
          title: i18next.t('profileScreen.personalData.0.title'),
          description: getItem(PageName.name),
        },
        {
          id: 2,
          iconNode: <img src={lifestyle} alt="" />,
          title: i18next.t('profileScreen.personalData.1.title'),
          description: getTitleFromPageConfig(getItem, PageName.physicalBuild, pagesConfig),
        },
        {
          id: 3,
          iconNode: <img src={level} alt="" />,
          title: i18next.t('profileScreen.personalData.2.title'),
          description: getTitleFromPageConfig(getItem, PageName.workout, pagesConfig),
        },
        {
          id: 4,
          iconNode: <img src={diet} alt="" />,
          title: i18next.t('profileScreen.personalData.3.title'),
          description: getTitleFromPageConfig(getItem, PageName.diet, pagesConfig),
        },
      ]
    },
  },
  footerTitle: i18next.t('profileScreen.footerTitle'),
  buttonName: i18next.t(`profileScreen.buttonName`),
}

export const weightLossProgram: TWeightLossPage = {
  title: i18next.t('weightLossProgram.title'),
  nextUrl: getPath(PageName.preparingPlan),
  weightDescription: i18next.t('weightLossProgram.weightDescription'),
  yourGoalTitle: i18next.t('weightLossProgram.yourGoalTitle'),
  CustomChart: (props) => (
    <WeightLossChartPilates
      {...props}
      justInTimeText={i18next.t('weightLossProgram.justInTimeText')}
      preposition={i18next.t('weightLossProgram.preposition')}
      textGoal={i18next.t('weightLossProgram.textGoal')}
    />
  ),
  policyBlock: true,
  buttonName: i18next.t(`weightLossProgram.buttonName`),
}

export const warmUpPage: TWarmUpPage = {
  tabTitle: i18next.t('warmUpPage.tabTitle'),
  title: () => {
    const name = getItem(PageName.name)
    if (name) {
      return `${name},`
    }
    return ''
  },
  pageDescription: i18next.t('warmUpPage.pageDescription'),
  buttonName: i18next.t('warmUpPage.buttonName'),
  nextUrl: getPath(PageName.selectPlan),
  policyBlock: true,
  childComponent: (() => {
    return (
      <>
        <WeightLossTimelineChart
          getItem={getItem}
          yourWeightText={i18next.t('warmUpPage.childComponent.yourWeightText')}
          yourWeightGoalText={i18next.t('warmUpPage.childComponent.yourWeightGoalText')}
          period={i18next.t('warmUpPage.childComponent.period')}
          chartName={i18next.t('warmUpPage.childComponent.chartName')}
          descriptionPlan={i18next.t('warmUpPage.childComponent.descriptionPlan')}
        />
      </>
    )
  })(),
}
